import { memo } from 'react';

import { Button } from '@picsart/design-system/components/Button';
import { IconGenAI } from '@picsart/design-system/foundation/icons/IconGenAI';
import { IconCoins } from '@picsart/design-system/foundation/icons/IconCoins';
import { IconBookmarkFilled } from '@picsart/design-system/foundation/icons/IconBookmarkFilled';
import { IconBookmarkOutline } from '@picsart/design-system/foundation/icons/IconBookmarkOutline';
import { useTranslator } from '@picsart/rc/services/localization';

import Spinner from '@/components/Spinner';
import usePresetCreditsCost from '@/hooks/usePresetCreditsCost';
import useSavedTemplates from '@/hooks/useSavedTemplates';
import useTouchpoint from '@/hooks/useTouchpoint';
import { ScreenType } from '@/types/analytics';
import useStyles from './styles';

import type { FeedActionBarProps } from './types';

const FeedActionBar = ({
  label,
  onUpload,
  isDisabled,
  isLoading,
  slug,
  shouldLoadCredits,
}: FeedActionBarProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const { creditsCost, isCreditsCostLoading } = usePresetCreditsCost({
    slug,
    isEnabled: shouldLoadCredits,
  });
  const { isPortfolioApp } = useTouchpoint();
  const { isSaved, toggleSaved } = useSavedTemplates();
  const saved = isSaved(slug);
  const SaveIcon = saved ? IconBookmarkFilled : IconBookmarkOutline;

  return (
    <div className={classes.actionBar}>
      {isPortfolioApp && (
        <button
          type="button"
          className={classes.saveButton}
          onClick={() => toggleSaved(slug, ScreenType.PRESET_FEED)}
          aria-label={saved ? 'Remove from saved' : 'Save'}
          data-testid="feed-save"
        >
          <SaveIcon className={classes.saveIcon} fill="currentColor" />
          <span className={classes.saveLabel}>{translate('Save')}</span>
        </button>
      )}

      <Button
        dataTestId="preset-feed-upload-button"
        className={classes.uploadButton}
        variant="filled"
        size="large"
        isFullWidth
        startIcon={IconGenAI}
        onClick={onUpload}
        isDisabled={isDisabled}
        isLoading={isLoading}
      >
        {label}
      </Button>

      {isCreditsCostLoading ? (
        <div className={classes.creditsChip}>
          <Spinner size="small" className={classes.creditsSpinner} />
        </div>
      ) : (
        !!creditsCost &&
        creditsCost > 0 && (
          <div className={classes.creditsChip}>
            <IconCoins className={classes.creditsIcon} fill="currentColor" />
            <span>{creditsCost}</span>
          </div>
        )
      )}
    </div>
  );
};

export default memo(FeedActionBar);
